/**
 * Progress rollup and parent bar spans.
 *
 * The plan called for a recursive CTE on the server. It lives here instead, as
 * a pure function shared by both sides, because the client already holds every
 * task in its store: computing rollup there is one O(n) pass that Vue recomputes
 * reactively, versus a server round-trip after every status change. Keeping it
 * in `shared/` means there is still exactly ONE implementation, and it unit
 * tests without a database.
 */
import { DONE_STATUSES, loggedHours, type Task } from "./types.js";

export interface Rollup {
  /** 0..1 across all leaf descendants, weighted by estHours (default 1). */
  percent: number;
  doneWeight: number;
  totalWeight: number;
  leafCount: number;
  /** Effective bar extent: own dates if set, else the span of descendants. */
  startMs: number | null;
  endMs: number | null;
  /** True when children spill outside the parent's own explicit dates. */
  overflows: boolean;
  /** Hours people logged on this task and everything under it. */
  spentHours: number;
  /**
   * Sum of EXPLICIT `estHours` over leaf descendants; null when nobody set one.
   * Deliberately not `totalWeight`: that defaults an unestimated leaf to 1 so
   * the percent stays meaningful, which would read as "1 hour" here.
   */
  estHours: number | null;
  descendantCount: number;
  /** Unfinished tasks anywhere under this one, not counting itself. */
  openDescendants: number;
  /**
   * The highest urgency on this task or anything unfinished under it — what the
   * tree sorts by. A done task contributes nothing: finished work is not pressing,
   * and a completed critical fix must not keep its parent at the top forever.
   */
  urgency: number;
}

/**
 * How much of a single leaf is finished, 0..1.
 *
 * `done` is absolute — a task marked done is finished whatever the slider was
 * left at, otherwise the board could show a done task at 40% forever. Below
 * that, an explicit `progress` wins, and a task nobody has touched falls back to
 * the original binary behaviour.
 *
 * The type says `number | null`, and this checks anyway. A task sent by a
 * server older than the progress column arrives with no `progress` key at all,
 * and `Math.max(0, undefined)` is NaN — which spreads silently through the
 * rollup into the bar geometry, where SVG treats an invalid x as 0 and slams the
 * bar to the left edge of the chart rather than failing loudly. Anything that is
 * not a real number means "nobody has set this".
 */
function leafFraction(task: Task): number {
  if (DONE_STATUSES.has(task.status)) return 1;
  const p = task.progress;
  if (typeof p !== "number" || !Number.isFinite(p)) return 0;
  return Math.min(1, Math.max(0, p));
}

/** A task's own urgency, 0 — below the scale's floor, meaning "contributes
 *  nothing" — when finished or when a server predating the column sent none.
 *  Same NaN guard as leafFraction. */
function ownUrgency(task: Task): number {
  if (DONE_STATUSES.has(task.status)) return 0;
  const u = task.urgency;
  return typeof u === "number" && Number.isFinite(u) ? u : 0;
}

/** Index children by parent, each list sorted by rank. */
export function childrenByParent(tasks: Iterable<Task>): Map<string | null, Task[]> {
  const map = new Map<string | null, Task[]>();
  for (const t of tasks) {
    const list = map.get(t.parentId);
    if (list) list.push(t);
    else map.set(t.parentId, [t]);
  }
  for (const list of map.values()) list.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
  return map;
}

/**
 * One post-order pass computing every task's rollup. Iterative rather than
 * recursive so a deeply nested tree cannot blow the stack — "infinitely nested"
 * is the headline feature, so the traversal has to actually survive it.
 */
export function computeRollups(tasks: Iterable<Task>): Map<string, Rollup> {
  const all = [...tasks];
  const kids = childrenByParent(all);
  const out = new Map<string, Rollup>();

  // Post-order via an explicit stack: push a node, then its children; the
  // reversed visit order guarantees children resolve before their parent.
  const order: Task[] = [];
  const stack: Task[] = [...(kids.get(null) ?? [])];
  while (stack.length) {
    const node = stack.pop()!;
    order.push(node);
    for (const c of kids.get(node.id) ?? []) stack.push(c);
  }

  for (let i = order.length - 1; i >= 0; i--) {
    const task = order[i]!;
    const children = kids.get(task.id) ?? [];

    let doneWeight = 0;
    let totalWeight = 0;
    let leafCount = 0;
    let descendantCount = 0;
    let openDescendants = 0;
    let childStart: number | null = null;
    let childEnd: number | null = null;
    let estHours: number | null = null;

    // A parent can be worked on directly, so its own time counts too — this is
    // not only an aggregate over the leaves.
    const own = task.time ?? [];
    let spentHours = loggedHours(own);
    let urgency = ownUrgency(task);

    if (children.length === 0) {
      // A leaf carries its own weight; estHours lets a 20h task outweigh a 1h one.
      totalWeight = task.estHours && task.estHours > 0 ? task.estHours : 1;
      doneWeight = totalWeight * leafFraction(task);
      leafCount = 1;
      if (task.estHours && task.estHours > 0) estHours = task.estHours;
    } else {
      for (const c of children) {
        const r = out.get(c.id);
        if (!r) continue; // unreachable in a well-formed tree
        doneWeight += r.doneWeight;
        totalWeight += r.totalWeight;
        leafCount += r.leafCount;
        descendantCount += r.descendantCount + 1;
        openDescendants += r.openDescendants + (DONE_STATUSES.has(c.status) ? 0 : 1);
        if (r.startMs !== null) childStart = childStart === null ? r.startMs : Math.min(childStart, r.startMs);
        if (r.endMs !== null) childEnd = childEnd === null ? r.endMs : Math.max(childEnd, r.endMs);
        if (r.estHours !== null) estHours = (estHours ?? 0) + r.estHours;
        spentHours += r.spentHours;
        urgency = Math.max(urgency, r.urgency);
      }
    }

    // Explicit dates win; otherwise a parent bar spans its descendants.
    const startMs = task.startMs ?? childStart;
    const endMs = task.endMs ?? childEnd;
    const overflows =
      (task.startMs !== null && childStart !== null && childStart < task.startMs) ||
      (task.endMs !== null && childEnd !== null && childEnd > task.endMs);

    out.set(task.id, {
      percent: totalWeight > 0 ? doneWeight / totalWeight : 0,
      doneWeight,
      totalWeight,
      leafCount,
      startMs,
      endMs,
      overflows,
      spentHours,
      estHours,
      descendantCount,
      openDescendants,
      urgency,
    });
  }

  return out;
}

/**
 * The child map with finished work taken out. A done task stays if anything
 * under it is still open: dropping it would orphan live tasks, or silently file
 * them under the wrong parent. Post-order and iterative, like the rollup.
 */
export function pruneDone(childMap: Map<string | null, Task[]>): Map<string | null, Task[]> {
  return keepWithAncestors(childMap, (t) => !DONE_STATUSES.has(t.status));
}

/**
 * The tree cut down to the tasks `keep` accepts, plus every ancestor of one —
 * a kept task shown without the parents it sits in would lose its context and
 * turn up as a root it isn't.
 */
export function keepWithAncestors(
  childMap: Map<string | null, Task[]>,
  keep: (task: Task) => boolean,
): Map<string | null, Task[]> {
  const kept = new Set<string>();
  const order: Task[] = [];
  const stack: Task[] = [...(childMap.get(null) ?? [])];
  while (stack.length) {
    const task = stack.pop()!;
    order.push(task);
    for (const c of childMap.get(task.id) ?? []) stack.push(c);
  }
  // Reverse pre-order visits every child before its parent.
  for (let i = order.length - 1; i >= 0; i--) {
    const task = order[i]!;
    if (keep(task) || (childMap.get(task.id) ?? []).some((c) => kept.has(c.id))) kept.add(task.id);
  }
  const out = new Map<string | null, Task[]>();
  for (const [parent, list] of childMap) out.set(parent, list.filter((t) => kept.has(t.id)));
  return out;
}

/**
 * Which heading a whole tree files under: the root's own answer, or the first
 * one any descendant gives. A parent rarely repeats what its children already
 * say — the repo and the assignee usually live on the leaves — so reading only
 * the root would put almost everything in the leftover bucket. Pre-order, so
 * it is the first answer a reader would have seen scanning down the tree.
 *
 * Shared because the board's grouping (web/src/stores/board.ts) and the phone
 * widget's (shared/myTasks.ts) must file the same tree under the same repo.
 */
export function dominantValue(
  root: Task,
  childMap: Map<string | null, Task[]>,
  of: (task: Task) => string | null,
): string | null {
  const stack: Task[] = [root];
  while (stack.length) {
    const task = stack.pop()!;
    const value = of(task);
    if (value) return value;
    const children = childMap.get(task.id) ?? [];
    for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]!);
  }
  return null;
}
